import { memo, useCallback, useMemo, useState } from 'react';
import { Box, Button, LinearProgress, Typography } from '@mui/material';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import { useDropzone } from 'react-dropzone';
import { useNotify } from 'react-admin';
import { useTranslation } from 'react-i18next';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { resolveSxProps } from 'utility/styles';
import { LabelWithTooltip } from '../components/LabelWithTooltip';
import { TDynamicFieldOnChange, DynamicFieldType } from '../DynamicFormField';
import { acceptImageMimeObject } from 'utility/constants';
import { useImageDynamicField } from '../hooks/useImageDynamicField';
import { getFieldLabel } from '../utility/functions';
import { ApiErrorResponse } from 'utility/types/dataProvider';

export type TImageValueObject = {
  id: string;
  width?: number;
  height?: number;
  type?: string;
  size?: number;
  url?: string;
};

export type ImageDynamicFieldProps = {
  fieldConfig: DynamicFieldType;
  value: string | TImageValueObject | null | undefined;
  handleValueChange: TDynamicFieldOnChange;
  disabled?: boolean;
  fieldSx?: SxProps<Theme>;
  showInitialPreview?: boolean; // Control whether to show saved image on initial load
  onUploadReject?: () => void;
  onUploadStart?: () => void;
  onUploadSuccess?: () => void;
  onUploadError?: () => void;
};

export const ImageDynamicField = memo(
  ({
    fieldConfig,
    value,
    handleValueChange,
    disabled = false,
    fieldSx,
    showInitialPreview = true, // Default to showing saved images
    onUploadReject,
    onUploadStart,
    onUploadSuccess,
    onUploadError,
  }: ImageDynamicFieldProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const notify = useNotify();
    const [uploadStatus, setUploadStatus] = useState<string>('');
    const [hasUploadedInSession, setHasUploadedInSession] = useState<boolean>(false);

    const { mutation, formatBinaryUploadResponse, extractBinaryId, generatePreviewUrl } =
      useImageDynamicField({
        setLoadingStatus: (status) => setUploadStatus(status || ''),
      });

    // Resolve styles
    const resolvedSx: SxProps<Theme> = useMemo(() => {
      return resolveSxProps(fieldSx, theme);
    }, [fieldSx, theme]);

    // Tooltip text
    const tooltipText = fieldConfig.tooltip || fieldConfig.help || '';

    // Extract binary ID from value (which is an object with id)
    const binaryId = useMemo(() => {
      const extracted = extractBinaryId(value);
      return extracted;
    }, [value, extractBinaryId]);

    // Has image when we have a binary ID AND (showInitialPreview is true OR user uploaded in this session)
    const hasImage = Boolean(binaryId) && (showInitialPreview || hasUploadedInSession);

    // Generate preview URL - prioritize 'url' property from value, then generate from binary ID
    const previewUrl = useMemo(() => {
      // If value has a url property (saved format), use it
      if (value && typeof value === 'object' && 'url' in value && value.url) {
        return value.url;
      }
      // Otherwise, generate preview URL from binary ID
      const generated = generatePreviewUrl(binaryId);
      return generated;
    }, [value, binaryId, generatePreviewUrl]);

    // Handle file drop accepted
    const handleFileDropAccepted = useCallback(
      async (acceptedFiles: File[]) => {
        if (acceptedFiles.length === 0) {
          return;
        }

        // Call onUploadStart prop callback.
        if (onUploadStart) {
          onUploadStart();
        }

        const file = acceptedFiles[0];

        try {
          const response = await mutation.mutateAsync(file);
          const formattedData = formatBinaryUploadResponse(response);

          if (formattedData) {
            notify(t('blox.general.success'), {
              type: 'success',
              messageArgs: { fileName: file.name },
            });

            // Mark that an image was uploaded in this session
            setHasUploadedInSession(true);

            // Call onUploadSuccess prop callback.
            if (onUploadSuccess) {
              onUploadSuccess();
            }

            // API expects an object with id, not just a string
            const valueToSet = {
              id: formattedData.binaryId,
              width: formattedData.width,
              height: formattedData.height,
              type: formattedData.type,
              size: formattedData.size,
              url: formattedData.previewUrl, // Include URL for immediate preview
            };

            handleValueChange(fieldConfig.name, valueToSet);
          }
        } catch (error) {
          const apiError = error as ApiErrorResponse;
          notify(apiError?.message || t('blox.general.upload_failed'), {
            type: 'error',
            messageArgs: { fileName: file.name },
          });
          // Call onUploadError prop callback.
          if (onUploadError) {
            onUploadError();
          }
        }
      },
      [
        onUploadStart,
        mutation,
        formatBinaryUploadResponse,
        notify,
        onUploadSuccess,
        handleValueChange,
        fieldConfig.name,
        onUploadError,
        t,
      ],
    );

    // Handle file drop rejected
    const handleFileDropRejected = useCallback(() => {
      notify(t('blox.editorial.image.invalid'), {
        type: 'error',
      });

      // Call onUploadReject prop callback
      if (onUploadReject) {
        onUploadReject();
      }
    }, [notify, onUploadReject, t]);

    const isUploading = mutation.isPending;

    // Setup dropzone
    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
      onDropAccepted: handleFileDropAccepted,
      onDropRejected: handleFileDropRejected,
      accept: acceptImageMimeObject,
      multiple: false,
      disabled: disabled || isUploading,
    });

    // Handle remove image
    const handleRemoveImage = useCallback(() => {
      // Reset upload state
      setHasUploadedInSession(false);
      // API expects an object with remove_binary flag
      handleValueChange(fieldConfig.name, { remove_binary: true });
    }, [fieldConfig.name, handleValueChange]);

    // Field label
    const fieldLabel = useMemo(() => {
      return getFieldLabel(fieldConfig);
    }, [fieldConfig]);

    return (
      <Box
        data-testid="image-dynamic-field-container"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          ...resolvedSx,
        }}
      >
        {/* Label */}
        {fieldLabel && (
          <LabelWithTooltip
            label={fieldLabel}
            tooltip={tooltipText}
          />
        )}

        {/* Dropzone */}
        <Box
          {...getRootProps()}
          data-testid="image-dropzone"
          sx={{
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'divider',
            borderRadius: 1,
            p: 2,
            textAlign: 'center',
            cursor: disabled || isUploading ? 'default' : 'pointer',
            backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
            transition: 'all 0.2s',
            height: 200,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            position: 'relative',
          }}
        >
          <input
            {...getInputProps()}
            data-testid="image-input"
          />

          {isUploading ? (
            <>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {uploadStatus || t('blox.dynamic_fields.image.uploading')}
              </Typography>
              <LinearProgress
                sx={{ width: '100%', mt: 1 }}
                data-testid="upload-progress"
              />
            </>
          ) : hasImage && previewUrl ? (
            <img
              src={previewUrl}
              alt={fieldConfig.label || 'Image'}
              data-testid="image-preview"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          ) : (
            <>
              <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {isDragActive
                  ? t('blox.dynamic_fields.image.dropzone.subtitle')
                  : t('blox.dynamic_fields.image.dropzone.caption')}
              </Typography>
            </>
          )}
        </Box>

        {/* Action buttons - shown below dropzone when image exists */}
        {hasImage && (
          <Box
            data-testid="image-action-buttons"
            sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}
          >
            <Button
              variant="text"
              size="small"
              onClick={(e) => {
                e.preventDefault();
                open();
              }}
              disabled={disabled}
              data-testid="replace-button"
            >
              {t('blox.actions.replace')}
            </Button>
            <Button
              variant="text"
              size="small"
              color="error"
              onClick={(e) => {
                e.preventDefault();
                handleRemoveImage();
              }}
              disabled={disabled}
              data-testid="remove-button"
            >
              {t('blox.actions.remove')}
            </Button>
          </Box>
        )}
      </Box>
    );
  },
);

ImageDynamicField.displayName = 'ImageDynamicField';
